"use strict";
/**
 * AST-style filter DSL для CRUD list-запросов.
 *
 * Используется в `q.<entity>.list { filter, limit, offset, sort, ... }` —
 * клиент собирает дерево условий, handler делает `applyFilter(ast, opts)` →
 * параметризованный SQL `WHERE` clause. Защита от injections в 3 эшелона:
 * zod-валидация (depth/leaves/types) → SQL handler (whitelist, prepared
 * statements) → Postgres RLS (last line, см. docs/todo-postgres-rls-migration.md).
 *
 * Подробная спецификация — `docs/todo-crud-filter-dsl.md`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CRUD_FILTER_LIMITS = exports.isCrudArrayFilterOp = exports.CrudArrayFilterOperators = exports.CrudFilterOperators = void 0;
/**
 * Стандартный набор операторов CRUD-фильтра. Domain может ограничить меньшим
 * subset'ом при создании schema (`crudFilterSchema(fields, customOps)`).
 *
 * Семантика:
 * - `eq` / `neq` — равенство / неравенство одиночного значения.
 * - `in` / `nin` — мембершип в массиве (max 100 элементов в zod-cap).
 * - `like` / `ilike` — pattern match с `%`/`_` wildcard'ами от клиента; по
 *   умолчанию (`likeMode: 'safe'`) спецсимволы экранируются (буквальное
 *   совпадение) — раскрыть wildcard клиенту можно только через admin-only
 *   `likeMode: 'raw'` (см. `ApplyFilterOpts` в filter-sql.ts).
 * - `contains` / `starts_with` / `ends_with` — безопасный substring-поиск:
 *   значение ВСЕГДА экранируется и оборачивается `%` сервером (SQL builder),
 *   клиент не может передать свой паттерн. Не читают `likeMode` — доступны
 *   любой роли.
 * - `gte` / `lte` / `gt` / `lt` — numeric / datetime comparison.
 * - `is_null` / `is_not_null` — null check без `value` поля.
 * - `any_eq` / `any_in` / `any_contains` (ROENT) — условия по **элементам массива**:
 *   «есть элемент, равный», «есть элемент из списка», «есть элемент, содержащий
 *   подстроку». Допустимы ТОЛЬКО на полях, объявленных `list.arrayFields`
 *   сущности (PG-массив `text[]`/`uuid[]`); на остальных полях zod их не пропускает.
 *   `any_eq`/`any_in` идут по GIN-индексу (`@>`/`&&`), `any_contains` — перебор.
 *
 * ⚠ АУДИТ 16.07 (J3, blast-radius, НЕ фикшено — помечено): `contains` в ДЕФОЛТНОМ
 * наборе → любая сущность, не сузившая `filterOps`, отдаёт substring-поиск
 * (`ILIKE '%…%'` = гарантированный seq-scan) по всем нефильтруемым-исключённым строкам —
 * для любой роли, у которой уже есть `list`/`aggregate`. Инъекций/утечек нет (значение
 * параметризовано, экранировано), риск чисто perf. Проаудировать/сузить `filterOps` на
 * крупных таблицах (chat_messages, sessions, customers) с широкими ролями по мере надобности.
 */
exports.CrudFilterOperators = [
    'eq',
    'neq',
    'in',
    'nin',
    'like',
    'ilike',
    'contains',
    'starts_with',
    'ends_with',
    'gte',
    'lte',
    'gt',
    'lt',
    'is_null',
    'is_not_null',
    'any_eq',
    'any_in',
    'any_contains',
];
/** Операторы по элементам массива — только для `list.arrayFields`. */
exports.CrudArrayFilterOperators = ['any_eq', 'any_in', 'any_contains'];
const isCrudArrayFilterOp = (op) => exports.CrudArrayFilterOperators.includes(op);
exports.isCrudArrayFilterOp = isCrudArrayFilterOp;
exports.DEFAULT_CRUD_FILTER_LIMITS = {
    maxDepth: 10,
    maxLeaves: 100,
    maxInArraySize: 100,
    maxStringLength: 256,
    maxLikePatternLength: 64,
};
//# sourceMappingURL=filter-types.js.map